import { FC, useCallback, useEffect, useMemo, useState } from "react";

import AsyncSearchableSelect from "components/AsyncSearchableSelect";
import Button from "components/Button";
import InfoCard from "components/InfoCard";
import Loader from "components/Loader";

import { RemoveUserRequest } from "entities/common";
import { UsersApi } from "services/http";
import { RootStore } from "store";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { hideModal } from "store/slices/modalSlice";
import { showToast } from "store/slices/toastSlice";
import {
  getUserDeletionInfoRequest,
  removeUserRequest,
  resetErrorsUser,
  resetSuccessfullyRemovedUser,
} from "store/slices/usersSlice";
import { QUERY_PARAMS } from "utils/constants";
import { QueryHelper } from "utils/helpers";

import styles from "../style.module.scss";

type ManagerSelection = { managerId: number | null; managerName: string | null };

type PropTypes = {
  onClose?: () => void;
  id?: number;
  companyId?: number;
  name?: string;
};

const emptySelection = (count: number): ManagerSelection[] =>
  [...new Array(count)].map(() => ({ managerId: null, managerName: null }));

const ModalRemoveContent: FC<PropTypes> = ({ onClose, id, name, companyId }) => {
  const dispatch = useAppDispatch();
  const { deletionInfo, isDeletionInfoLoading, isLoading, successfullyRemovedId, errors } = useAppSelector(
    (state: RootStore) => state.users,
  );

  const managedPortfolios = useMemo(() => deletionInfo?.managed_portfolios ?? [], [deletionInfo]);
  const managedAssets = useMemo(() => deletionInfo?.managed_assets ?? [], [deletionInfo]);
  const displayName = deletionInfo?.full_name || name;

  const [portfolioManagers, setPortfolioManagers] = useState<ManagerSelection[]>([]);
  const [assetManagers, setAssetManagers] = useState<ManagerSelection[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(getUserDeletionInfoRequest(Number(id)));
    }
  }, [dispatch, id]);

  useEffect(() => {
    dispatch(resetErrorsUser());
    dispatch(resetSuccessfullyRemovedUser());
  }, [dispatch]);

  useEffect(() => {
    if (deletionInfo) {
      setPortfolioManagers(emptySelection(deletionInfo.managed_portfolios.length));
      setAssetManagers(emptySelection(deletionInfo.managed_assets?.length || 0));
    }
  }, [deletionInfo]);

  const handleManagerSearch = useCallback(
    async (search: string, page: number) => {
      try {
        const { data } = await UsersApi.getList(
          QueryHelper.getQueryString({
            [QUERY_PARAMS.SEARCH]: search || undefined,
            [QUERY_PARAMS.PAGE]: page,
            [QUERY_PARAMS.PAGE_SIZE]: 50,
            [QUERY_PARAMS.COMPANY_ID]: companyId,
          }),
        );

        const options = (data?.items || [])
          .filter((user) => user?.id !== id)
          .map((user) => ({ value: String(user?.id), label: user?.full_name }));
        const hasMore = data?.total ? page * 50 < data.total : false;

        return { options, hasMore };
      } catch {
        return { options: [], hasMore: false };
      }
    },
    [companyId, id],
  );

  const handlePortfolioManagerChange = useCallback(
    (index: number) => (value: string, label: string) => {
      setPortfolioManagers((prev) => {
        const next = [...prev];
        next[index] = { managerId: Number(value), managerName: label || "" };
        return next;
      });
    },
    [],
  );

  const handleAssetManagerChange = useCallback(
    (index: number) => (value: string, label: string) => {
      setAssetManagers((prev) => {
        const next = [...prev];
        next[index] = { managerId: Number(value), managerName: label || "" };
        return next;
      });
    },
    [],
  );

  const handleDelete = () => {
    if (managedPortfolios.length && !portfolioManagers.every((m) => m.managerId !== null)) {
      setError("Please select new managers for all portfolios");
      return;
    }

    if (managedAssets.length && !assetManagers.every((m) => m.managerId !== null)) {
      setError("Please select new managers for all assets");
      return;
    }

    setError(null);

    if (!id) {
      return;
    }

    const body: RemoveUserRequest = {
      reassignments: managedPortfolios.map((portfolio, index) => ({
        portfolio_id: portfolio.portfolio_id,
        new_manager_id: Number(portfolioManagers[index]?.managerId),
      })),
      asset_reassignments: managedAssets.map((asset, index) => ({
        asset_id: asset.asset_id,
        new_manager_id: Number(assetManagers[index]?.managerId),
      })),
    };

    setSubmitted(true);
    dispatch(removeUserRequest({ userId: Number(id), body }));
  };

  const handleCancel = () => {
    onClose?.();
    dispatch(hideModal());
  };

  useEffect(() => {
    if (submitted && successfullyRemovedId) {
      setSubmitted(false);
      dispatch(hideModal());
      dispatch(resetSuccessfullyRemovedUser());
      dispatch(
        showToast({
          id: "successfully-deleted-user",
          message: "The user was successfully deleted.",
          type: "success",
          duration: 3000,
        }),
      );
    }
  }, [submitted, successfullyRemovedId, dispatch]);

  useEffect(() => {
    if (submitted && errors) {
      setSubmitted(false);
      dispatch(resetErrorsUser());
      dispatch(
        showToast({
          id: "error-deleting-user",
          message: "Failed to delete user. Please try again later.",
          type: "error",
          duration: 3000,
        }),
      );
    }
  }, [submitted, errors, dispatch]);

  return (
    <div className={styles.content}>
      <p className={styles.contentTitle}>This action cannot be undone</p>

      <InfoCard color="red" variant="red" size="small" className={styles.contentWarning}>
        <p className={styles.contentWarningTitle}>Warning</p>

        <span>
          Are you sure you want to delete this user from the company? They will lose all access to portfolios and
          assets.
        </span>
      </InfoCard>

      {isDeletionInfoLoading && (
        <div className={styles.contentLoading}>
          <Loader isLoading={true} inline />
        </div>
      )}

      {!isDeletionInfoLoading && managedPortfolios.length > 0 && (
        <div className={styles.assignment}>
          <div className={styles.assignmentItem}>
            <p className={styles.assignmentItemFirstTitle}>Portfolio manager assignment</p>

            <InfoCard color="orange" variant="orange" size="small" className={styles.assignmentInfo}>
              <p className={styles.contentInfoTitle}>{displayName}</p>

              <span>
                Is currently managing{" "}
                {managedPortfolios.length !== 1 ? `${managedPortfolios.length} portfolios` : "1 portfolio"}. Please
                assign new managers for these portfolios.
              </span>
            </InfoCard>
          </div>

          {managedPortfolios.map((portfolio, index) => (
            <div className={styles.assignmentItem} key={portfolio.portfolio_id}>
              <p className={styles.assignmentItemTitle}>{portfolio.portfolio_name}</p>
              <AsyncSearchableSelect
                onSearch={handleManagerSearch}
                onChange={handlePortfolioManagerChange(index)}
                label="New portfolio manager"
                placeholder="Select"
                selectedLabel={portfolioManagers[index]?.managerName || ""}
                value={portfolioManagers[index]?.managerId?.toString() || ""}
              />
            </div>
          ))}
        </div>
      )}

      {!isDeletionInfoLoading && managedAssets.length > 0 && (
        <div className={styles.assignment}>
          <div className={styles.assignmentItem}>
            <p className={styles.assignmentItemFirstTitle}>Asset manager assignment</p>

            <InfoCard color="orange" variant="orange" size="small" className={styles.assignmentInfo}>
              <p className={styles.contentInfoTitle}>{displayName}</p>

              <span>
                Is currently managing {managedAssets.length !== 1 ? `${managedAssets.length} assets` : "1 asset"}.
                Please assign new managers for these assets.
              </span>
            </InfoCard>
          </div>

          {managedAssets.map((asset, index) => (
            <div className={styles.assignmentItem} key={asset.asset_id}>
              <p className={styles.assignmentItemTitle}>{asset.asset_name}</p>
              <AsyncSearchableSelect
                onSearch={handleManagerSearch}
                onChange={handleAssetManagerChange(index)}
                label="New asset manager"
                placeholder="Select"
                selectedLabel={assetManagers[index]?.managerName || ""}
                value={assetManagers[index]?.managerId?.toString() || ""}
              />
            </div>
          ))}
        </div>
      )}

      {!!error && (
        <InfoCard color="red" variant="red" size="small" className={styles.contentWarning}>
          <span>{error}</span>
        </InfoCard>
      )}

      <div className={styles.buttons}>
        <Button variant="secondary" onClick={handleCancel} disabled={isLoading}>
          Cancel
        </Button>

        <Button variant="primary" onClick={handleDelete} disabled={isLoading}>
          Delete
        </Button>
      </div>
    </div>
  );
};

export default ModalRemoveContent;
